import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { base_url } from "../utils/axiosConfig";
import { CurrencyContext } from "../features/context/CurrencyContext";
import { useNavigate } from "react-router-dom";

const hasRepeatedDigits = (price) => {
  if (!price) return true;
  return /(\d)\1{4,}/.test(String(price));
};

const getProductPrice = (product) => {
  return (
    product?.price?.amount ??
    product?.variations?.[0]?.price?.amount ??
    0
  );
};

const isValidProduct = (product) => {
  const price = getProductPrice(product);

  return (
    product?.isActive !== false &&
    price > 0 &&
    !hasRepeatedDigits(price)
  );
};

const getProductImage = (product) => {
  return (
    product?.variations?.[0]?.images?.[0] ||
    product?.images?.[0] ||
    product?.image ||
    "/placeholder.png"
  );
};

const getProductName = (product) => {
  return (
    product?.productName ||
    product?.name ||
    "Healthcare Product"
  );
};

const getProductDescription = (product) => {
  return (
    product?.shortDescription ||
    product?.description ||
    "Explore this healthcare product and view complete product details."
  );
};

function Shop() {
  const navigate = useNavigate();

  const { convertPrice, getCurrencySymbol } =
    useContext(CurrencyContext);

  const [products, setProducts] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("default");

  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  const [selectedCategory, setSelectedCategory] =
    useState(null);

  const [selectedSubCategory, setSelectedSubCategory] =
    useState(null);

  const [selectedSubSubCategory, setSelectedSubSubCategory] =
    useState(null);

  const [selectedSubSubSubCategory, setSelectedSubSubSubCategory] =
    useState(null);

  /*
   * =========================================================
   * SECTION REFERENCES
   * =========================================================
   */

  const sectionRefs = useRef({});

  /*
   * =========================================================
   * SMOOTH SCROLL
   * =========================================================
   */

  const scrollToSection = (id) => {
    if (!id) return;

    setTimeout(() => {
      const element = sectionRefs.current[id];

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 200);
  };

  /*
   * =========================================================
   * FETCH PRODUCTS
   * =========================================================
   */

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          `${base_url}product/getCategory?page=1&limit=100&sortingColumn=createdAt&sortingOrder=asc`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch products");
        }

        const result = await response.json();

        const categories = result?.data || [];

        const tree = categories
          .filter(
            (category) =>
              category?.isActive !== false
          )
          .map((category) => {
            const subCategories =
              category?.subCategories || [];

            return {
              id: category?._id,

              name:
                category?.categoryName ||
                category?.name ||
                "Uncategorized",

              createdAt: category?.createdAt,

              subCategories:
                subCategories
                  .filter(
                    (sub) =>
                      sub?.isActive !== false
                  )
                  .map((sub) => {
                    const subSubCategories =
                      sub?.subSubCategories || [];

                    return {
                      id: sub?._id,

                      name:
                        sub?.subCategoryName ||
                        sub?.name ||
                        "Uncategorized",

                      products: (
                        sub?.products || []
                      ).filter(isValidProduct),

                      subSubCategories:
                        subSubCategories
                          .filter(
                            (ss) =>
                              ss?.isActive !== false
                          )
                          .map((ss) => {
                            const subSubSubCategories =
                              ss?.subSubSubCategories ||
                              [];

                            return {
                              id: ss?._id,

                              name:
                                ss?.subSubCategoryName ||
                                ss?.name ||
                                "Uncategorized",

                              products: (
                                ss?.products || []
                              ).filter(
                                isValidProduct
                              ),

                              subSubSubCategories:
                                subSubSubCategories
                                  .filter(
                                    (sss) =>
                                      sss?.isActive !==
                                      false
                                  )
                                  .map((sss) => ({
                                    id: sss?._id,

                                    name:
                                      sss?.subSubSubCategoryName ||
                                      sss?.name ||
                                      "Uncategorized",

                                    products: (
                                      sss?.products ||
                                      []
                                    ).filter(
                                      isValidProduct
                                    ),
                                  }))
                                  .filter(
                                    (sss) =>
                                      sss.products
                                        .length > 0
                                  ),
                            };
                          })
                          .filter(
                            (ss) =>
                              ss.products.length >
                                0 ||
                              ss
                                .subSubSubCategories
                                .length > 0
                          ),
                    };
                  })
                  .filter(
                    (sub) =>
                      sub.products.length > 0 ||
                      sub.subSubCategories.length > 0
                  ),
            };
          })
          .filter(
            (category) =>
              category.subCategories.length > 0
          );

        setCategoryTree(tree);

        /*
         * =====================================================
         * FLATTEN ALL PRODUCTS
         * =====================================================
         */

        const allProducts = [];

        tree.forEach((category) => {
          category.subCategories.forEach(
            (sub) => {
              sub.products.forEach((product) => {
                allProducts.push({
                  ...product,

                  categoryId: category.id,
                  categoryName: category.name,

                  subCategoryId: sub.id,
                  subCategoryName: sub.name,

                  categoryCreatedAt:
                    category.createdAt,
                });
              });

              sub.subSubCategories.forEach(
                (ss) => {
                  ss.products.forEach(
                    (product) => {
                      allProducts.push({
                        ...product,

                        categoryId:
                          category.id,

                        categoryName:
                          category.name,

                        subCategoryId:
                          sub.id,

                        subCategoryName:
                          sub.name,

                        subSubCategoryId:
                          ss.id,

                        subSubCategoryName:
                          ss.name,

                        categoryCreatedAt:
                          category.createdAt,
                      });
                    }
                  );

                  ss.subSubSubCategories.forEach(
                    (sss) => {
                      sss.products.forEach(
                        (product) => {
                          allProducts.push({
                            ...product,

                            categoryId:
                              category.id,

                            categoryName:
                              category.name,

                            subCategoryId:
                              sub.id,

                            subCategoryName:
                              sub.name,

                            subSubCategoryId:
                              ss.id,

                            subSubCategoryName:
                              ss.name,

                            subSubSubCategoryId:
                              sss.id,

                            subSubSubCategoryName:
                              sss.name,

                            categoryCreatedAt:
                              category.createdAt,
                          });
                        }
                      );
                    }
                  );
                }
              );
            }
          );
        });

        setProducts(allProducts);
      } catch (err) {
        console.error(err);

        setError(
          err?.message ||
            "Something went wrong"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  /*
   * =========================================================
   * CATEGORY CLICK
   * =========================================================
   */

  const handleCategoryClick = (category) => {
    const isSame =
      selectedCategory?.id === category.id;

    setSelectedCategory(
      isSame ? null : category
    );

    setSelectedSubCategory(null);
    setSelectedSubSubCategory(null);
    setSelectedSubSubSubCategory(null);

    scrollToSection(category.id);
  };

  /*
   * =========================================================
   * SUB CATEGORY CLICK
   * =========================================================
   */

  const handleSubCategoryClick = (sub) => {
    const isSame =
      selectedSubCategory?.id === sub.id;

    setSelectedSubCategory(
      isSame ? null : sub
    );

    setSelectedSubSubCategory(null);
    setSelectedSubSubSubCategory(null);

    scrollToSection(sub.id);
  };

  /*
   * =========================================================
   * SUB SUB CATEGORY CLICK
   * =========================================================
   */

  const handleSubSubCategoryClick = (ss) => {
    const isSame =
      selectedSubSubCategory?.id === ss.id;

    setSelectedSubSubCategory(
      isSame ? null : ss
    );

    setSelectedSubSubSubCategory(null);

    scrollToSection(ss.id);
  };

  /*
   * =========================================================
   * SUB SUB SUB CATEGORY CLICK
   * =========================================================
   */

  const handleSubSubSubCategoryClick = (sss) => {
    const isSame =
      selectedSubSubSubCategory?.id ===
      sss.id;

    setSelectedSubSubSubCategory(
      isSame ? null : sss
    );

    scrollToSection(sss.id);
  };

  /*
   * =========================================================
   * ALL CATEGORIES
   * =========================================================
   */

  const handleAllCategories = () => {
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedSubSubCategory(null);
    setSelectedSubSubSubCategory(null);

    setSearchTerm("");
    setMinPrice("");
    setMaxPrice("");
    setSortOption("default");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /*
   * =========================================================
   * FILTER PRODUCTS
   * =========================================================
   */

  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (selectedCategory) {
      result = result.filter(
        (product) =>
          product.categoryId ===
          selectedCategory.id
      );
    }

    if (selectedSubCategory) {
      result = result.filter(
        (product) =>
          product.subCategoryId ===
          selectedSubCategory.id
      );
    }

    if (selectedSubSubCategory) {
      result = result.filter(
        (product) =>
          product.subSubCategoryId ===
          selectedSubSubCategory.id
      );
    }

    if (selectedSubSubSubCategory) {
      result = result.filter(
        (product) =>
          product.subSubSubCategoryId ===
          selectedSubSubSubCategory.id
      );
    }

    if (searchTerm.trim()) {
      const search =
        searchTerm.toLowerCase();

      result = result.filter((product) => {
        return (
          getProductName(product)
            .toLowerCase()
            .includes(search) ||
          product?.categoryName
            ?.toLowerCase()
            .includes(search) ||
          product?.subCategoryName
            ?.toLowerCase()
            .includes(search) ||
          product?.subSubCategoryName
            ?.toLowerCase()
            .includes(search) ||
          product?.subSubSubCategoryName
            ?.toLowerCase()
            .includes(search)
        );
      });
    }

    if (minPrice !== "") {
      result = result.filter(
        (product) =>
          getProductPrice(product) >=
          Number(minPrice)
      );
    }

    if (maxPrice !== "") {
      result = result.filter(
        (product) =>
          getProductPrice(product) <=
          Number(maxPrice)
      );
    }

    if (sortOption === "low-high") {
      result.sort(
        (a, b) =>
          getProductPrice(a) -
          getProductPrice(b)
      );
    }

    if (sortOption === "high-low") {
      result.sort(
        (a, b) =>
          getProductPrice(b) -
          getProductPrice(a)
      );
    }

    return result;
  }, [
    products,
    selectedCategory,
    selectedSubCategory,
    selectedSubSubCategory,
    selectedSubSubSubCategory,
    searchTerm,
    minPrice,
    maxPrice,
    sortOption,
  ]);

  /*
   * =========================================================
   * BUILD DISPLAY TREE
   * =========================================================
   */

  const displayTree = useMemo(() => {
    const tree = {};

    filteredProducts.forEach((product) => {
      const categoryId =
        product.categoryId ||
        "uncategorized";

      const subCategoryId =
        product.subCategoryId ||
        "no-subcategory";

      if (!tree[categoryId]) {
        tree[categoryId] = {
          id: categoryId,

          name:
            product.categoryName ||
            "Uncategorized",

          subCategories: {},
        };
      }

      if (
        !tree[categoryId].subCategories[
          subCategoryId
        ]
      ) {
        tree[categoryId].subCategories[
          subCategoryId
        ] = {
          id: subCategoryId,

          name:
            product.subCategoryName ||
            "Uncategorized",

          products: [],

          subSubCategories: {},
        };
      }

      const sub =
        tree[categoryId].subCategories[
          subCategoryId
        ];

      if (product.subSubCategoryId) {
        const ssId =
          product.subSubCategoryId;

        if (!sub.subSubCategories[ssId]) {
          sub.subSubCategories[ssId] = {
            id: ssId,

            name:
              product.subSubCategoryName ||
              "Uncategorized",

            products: [],

            subSubSubCategories: {},
          };
        }

        const ss =
          sub.subSubCategories[ssId];

        if (
          product.subSubSubCategoryId
        ) {
          const sssId =
            product.subSubSubCategoryId;

          if (
            !ss.subSubSubCategories[
              sssId
            ]
          ) {
            ss.subSubSubCategories[
              sssId
            ] = {
              id: sssId,

              name:
                product.subSubSubCategoryName ||
                "Uncategorized",

              products: [],
            };
          }

          ss.subSubSubCategories[
            sssId
          ].products.push(product);
        } else {
          ss.products.push(product);
        }
      } else {
        sub.products.push(product);
      }
    });

    return Object.values(tree).map(
      (category) => ({
        ...category,

        subCategories: Object.values(
          category.subCategories
        ).map((sub) => ({
          ...sub,

          subSubCategories:
            Object.values(
              sub.subSubCategories
            ).map((ss) => ({
              ...ss,

              subSubSubCategories:
                Object.values(
                  ss.subSubSubCategories
                ),
            })),
        })),
      })
    );
  }, [filteredProducts]);

  /*
   * =========================================================
   * PRODUCT DETAILS
   * =========================================================
   */

  const handleViewDetails = (product) => {
    navigate(
      `/product-details/${product._id}`,
      {
        state: {
          product,
        },
      }
    );
  };

  /*
   * =========================================================
   * PRODUCT CARD
   * =========================================================
   */

  const renderProductCard = (product) => {
    const price =
      getProductPrice(product);

    const image =
      getProductImage(product);

    const name =
      getProductName(product);

    const description =
      getProductDescription(product);

    return (
      <div
        key={product._id}
        className="group w-full"
        style={{
          perspective: "1200px",
          height: "335px",
        }}
      >
        <div
          className="relative w-full h-full transition-transform duration-700 ease-in-out group-hover:[transform:rotateY(180deg)]"
          style={{
            transformStyle:
              "preserve-3d",
          }}
        >
          {/* =================================================
              FRONT SIDE
          ================================================= */}

          <div
            className="absolute inset-0 w-full h-full bg-white rounded-2xl overflow-hidden border border-green-100 shadow-sm"
            style={{
              backfaceVisibility:
                "hidden",
              WebkitBackfaceVisibility:
                "hidden",
            }}
          >
            {/* IMAGE */}

            <div
              className="relative bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center overflow-hidden"
              style={{
                height: "190px",
              }}
            >
              <span className="absolute top-3 left-3 z-10 bg-[#35A93E] text-white text-[9px] font-bold px-3 py-1 rounded-full shadow-sm">
                AVAILABLE
              </span>

              <img
                src={image}
                alt={name}
                className="object-contain transition-transform duration-500 group-hover:scale-105"
                style={{
                  width: "190px",
                  height: "190px",
                  objectFit: "contain",
                  padding: "14px",
                }}
              />

              <span className="absolute bottom-2 right-2 bg-white/90 text-[#35A93E] text-[9px] font-medium px-2 py-1 rounded-full shadow-sm">
                Hover to view
              </span>
            </div>

            {/* FRONT CONTENT */}

            <div className="px-4 py-3 h-[145px] flex flex-col">

              <h3 className="text-sm font-semibold text-gray-800 leading-5 line-clamp-2 min-h-[40px]">
                {name}
              </h3>

              <div className="mt-2">
                <p className="text-[9px] uppercase tracking-wider text-gray-400">
                  PRICE
                </p>

                <p className="text-lg font-bold text-[#35A93E] leading-6">
                  {getCurrencySymbol()}
                  {convertPrice(price)}
                </p>
              </div>

              <div className="mt-auto flex items-center justify-between">
                <span className="bg-green-50 text-[#35A93E] text-[8px] font-bold px-2.5 py-1 rounded-full">
                  AVAILABLE
                </span>

                <span className="text-[10px] text-gray-400">
                  View details →
                </span>
              </div>
            </div>
          </div>

          {/* =================================================
              BACK SIDE
          ================================================= */}

          <div
            className="absolute inset-0 w-full h-full rounded-2xl overflow-hidden shadow-xl text-white"
            style={{
              backfaceVisibility:
                "hidden",
              WebkitBackfaceVisibility:
                "hidden",
              transform:
                "rotateY(180deg)",
              background:
                "linear-gradient(135deg, #146b29 0%, #35A93E 55%, #63c96b 100%)",
            }}
          >
            {/* DECORATION */}

            <div className="absolute -right-16 -top-16 w-40 h-40 rounded-full bg-white/10" />

            <div className="absolute -left-20 -bottom-20 w-48 h-48 rounded-full bg-white/10" />

            {/* BACK CONTENT */}

            <div className="relative z-10 h-full px-5 py-5 flex flex-col">

              <p className="text-[8px] uppercase tracking-[0.18em] text-green-100 flex-shrink-0">
                LIFEONPLUS HEALTHCARE
              </p>

              <h3 className="text-[17px] font-bold leading-[21px] mt-2 line-clamp-2 min-h-[42px] flex-shrink-0">
                {name}
              </h3>

              <div className="w-full h-px bg-white/20 mt-3 flex-shrink-0" />

              <p className="text-[11px] text-green-50 leading-[17px] mt-3 line-clamp-4 min-h-[68px]">
                {description}
              </p>

              <div className="mt-auto flex-shrink-0">

                <div className="flex items-center justify-between mb-3 px-1">

                  <span className="text-[10px] text-green-100 uppercase tracking-wide">
                    Price
                  </span>

                  <span className="text-lg font-bold whitespace-nowrap">
                    {getCurrencySymbol()}
                    {convertPrice(price)}
                  </span>

                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();

                    handleViewDetails(
                      product
                    );
                  }}
                  className="w-full h-[42px] bg-white text-[#167a2c] font-bold text-[13px] rounded-xl shadow-md hover:bg-green-50 hover:scale-[1.02] transition-all duration-300"
                >
                  View Details →
                </button>

                <p className="text-[8px] text-center text-green-100 mt-2">
                  Explore complete product
                  details
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /*
   * =========================================================
   * LOADING
   * =========================================================
   */

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">

          <div className="w-10 h-10 border-4 border-[#35A93E] border-t-transparent rounded-full animate-spin mx-auto" />

          <p className="mt-3 text-sm text-gray-500">
            Loading products...
          </p>

        </div>
      </div>
    );
  }

  /*
   * =========================================================
   * ERROR
   * =========================================================
   */

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">
          Error: {error}
        </p>
      </div>
    );
  }

  /*
   * =========================================================
   * MAIN UI
   * =========================================================
   */

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f3faf4] via-white to-[#e9f8eb] pb-12">

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-3">

        {/* SHOP HEADER */}

        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#146b29] via-[#35A93E] to-[#65c96c] px-5 py-4 mb-4 shadow-md">

          <div className="absolute -right-10 -top-16 w-64 h-64 rounded-full bg-white/10" />

          <div className="relative">

            <p className="text-green-100 text-[10px]">
              Quality healthcare products
            </p>

            <h1 className="text-xl font-bold text-white">
              Shop Our Products
            </h1>

            <p className="text-green-50 text-[10px] mt-1">
              Explore our wide range of medical and healthcare essentials.
            </p>

          </div>
        </div>

        {/* SEARCH / FILTER */}

        <div className="bg-white rounded-2xl shadow-lg border border-green-100 p-4 mb-5">

          <div className="flex flex-col lg:flex-row gap-3">

            <input
              type="text"
              placeholder="Search products or categories..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(
                  e.target.value
                )
              }
              className="flex-1 px-5 py-3 bg-green-50 border border-green-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#35A93E]/30"
            />

            <select
              value={sortOption}
              onChange={(e) =>
                setSortOption(
                  e.target.value
                )
              }
              className="px-4 py-3 bg-green-50 border border-green-100 rounded-xl text-sm"
            >
              <option value="default">
                Sort Products
              </option>

              <option value="low-high">
                Price: Low → High
              </option>

              <option value="high-low">
                Price: High → Low
              </option>
            </select>

            <input
              type="number"
              placeholder="Min Price"
              value={minPrice}
              onChange={(e) =>
                setMinPrice(
                  e.target.value
                )
              }
              className="lg:w-28 px-3 py-3 bg-green-50 border border-green-100 rounded-xl text-sm"
            />

            <input
              type="number"
              placeholder="Max Price"
              value={maxPrice}
              onChange={(e) =>
                setMaxPrice(
                  e.target.value
                )
              }
              className="lg:w-28 px-3 py-3 bg-green-50 border border-green-100 rounded-xl text-sm"
            />

            <button
              onClick={
                handleAllCategories
              }
              className="px-5 py-3 bg-gray-100 hover:bg-red-50 hover:text-red-500 rounded-xl text-sm font-medium transition"
            >
              Reset
            </button>

          </div>
        </div>

        {/* MAIN LAYOUT */}

        <div className="flex flex-col lg:flex-row gap-5">

          {/* SIDEBAR */}

          <aside className="w-full lg:w-64 flex-shrink-0">

            <div className="bg-white rounded-2xl shadow-lg border border-green-100 overflow-hidden sticky top-4">

              <div className="bg-gradient-to-r from-[#146b29] to-[#35A93E] px-4 py-3">

                <h3 className="text-white font-bold text-sm">
                  Categories
                </h3>

                <p className="text-green-100 text-[10px]">
                  Browse products easily
                </p>

              </div>

              <div className="p-2 max-h-[70vh] overflow-y-auto">

                {/* ALL CATEGORIES */}

                <button
                  onClick={
                    handleAllCategories
                  }
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-semibold mb-1 transition ${
                    !selectedCategory
                      ? "bg-[#35A93E] text-white shadow-md"
                      : "bg-gray-50 text-gray-700 hover:bg-green-50"
                  }`}
                >
                  🛍️ All Categories
                </button>

                {/* CATEGORIES */}

                {categoryTree.map(
                  (category) => {
                    const categorySelected =
                      selectedCategory?.id ===
                      category.id;

                    return (
                      <div
                        key={
                          category.id
                        }
                      >

                        <button
                          onClick={() =>
                            handleCategoryClick(
                              category
                            )
                          }
                          className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-lg text-sm mb-1 transition ${
                            categorySelected
                              ? "bg-green-100 text-[#167a2c] font-bold"
                              : "text-gray-700 hover:bg-green-50"
                          }`}
                        >

                          <span className="truncate">
                            {
                              category.name
                            }
                          </span>

                          <span className="text-xs">
                            {categorySelected
                              ? "⌄"
                              : "›"}
                          </span>

                        </button>

                        {categorySelected &&
                          category.subCategories?.map(
                            (sub) => {
                              const subSelected =
                                selectedSubCategory?.id ===
                                sub.id;

                              return (
                                <div
                                  key={
                                    sub.id
                                  }
                                  className="ml-3 border-l-2 border-green-100 pl-2"
                                >

                                  <button
                                    onClick={() =>
                                      handleSubCategoryClick(
                                        sub
                                      )
                                    }
                                    className={`w-full text-left px-3 py-1.5 rounded-md text-xs mb-1 transition ${
                                      subSelected
                                        ? "bg-[#35A93E] text-white font-semibold"
                                        : "text-gray-600 hover:bg-green-50"
                                    }`}
                                  >
                                    {
                                      sub.name
                                    }
                                  </button>

                                  {subSelected &&
                                    sub.subSubCategories?.map(
                                      (ss) => {
                                        const ssSelected =
                                          selectedSubSubCategory?.id ===
                                          ss.id;

                                        return (
                                          <div
                                            key={
                                              ss.id
                                            }
                                            className="ml-2"
                                          >

                                            <button
                                              onClick={() =>
                                                handleSubSubCategoryClick(
                                                  ss
                                                )
                                              }
                                              className={`w-full text-left px-2.5 py-1 rounded-md text-[11px] mb-1 transition ${
                                                ssSelected
                                                  ? "bg-green-100 text-[#167a2c] font-bold"
                                                  : "text-gray-500 hover:bg-green-50"
                                              }`}
                                            >
                                              {
                                                ss.name
                                              }
                                            </button>

                                            {ssSelected &&
                                              ss.subSubSubCategories?.map(
                                                (
                                                  sss
                                                ) => (
                                                  <button
                                                    key={
                                                      sss.id
                                                    }
                                                    onClick={() =>
                                                      handleSubSubSubCategoryClick(
                                                        sss
                                                      )
                                                    }
                                                    className={`w-full text-left ml-2 px-2 py-1 rounded-md text-[10px] mb-1 transition ${
                                                      selectedSubSubSubCategory?.id ===
                                                      sss.id
                                                        ? "bg-[#35A93E] text-white"
                                                        : "text-gray-500 hover:bg-green-50"
                                                    }`}
                                                  >
                                                    {
                                                      sss.name
                                                    }
                                                  </button>
                                                )
                                              )}

                                          </div>
                                        );
                                      }
                                    )}

                                </div>
                              );
                            }
                          )}

                      </div>
                    );
                  }
                )}

              </div>
            </div>
          </aside>

          {/* PRODUCTS AREA */}

          <main className="flex-1 min-w-0">

            {/* PRODUCT COUNT */}

            <div className="bg-white rounded-2xl shadow-sm border border-green-100 px-5 py-4 mb-5 flex items-center justify-between">

              <div>

                <p className="text-xs text-gray-400">
                  Products available
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mt-1">
                  {filteredProducts.length}{" "}
                  Products
                </h2>

              </div>

              <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center text-xl">
                🛍️
              </div>

            </div>

            {/* NO PRODUCTS */}

            {filteredProducts.length ===
            0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-16 text-center">

                <div className="text-5xl mb-4">
                  🔍
                </div>

                <h3 className="font-bold text-gray-700">
                  No products found
                </h3>

                <button
                  onClick={
                    handleAllCategories
                  }
                  className="mt-5 bg-[#35A93E] text-white px-5 py-3 rounded-xl"
                >
                  Clear Filters
                </button>

              </div>
            ) : (
              <div className="space-y-6">

                {displayTree.map(
                  (category) => (

                    <section
                      key={
                        category.id
                      }
                      ref={(element) => {
                        sectionRefs.current[
                          category.id
                        ] = element;
                      }}
                      className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-green-100 scroll-mt-24"
                    >

                      {/* CATEGORY TITLE */}

                      <div className="flex items-center gap-3 mb-5">

                        <div className="w-2 h-9 bg-[#35A93E] rounded-full" />

                        <div>

                          <p className="text-[9px] text-gray-400 uppercase tracking-wider">
                            Category
                          </p>

                          <h2 className="text-xl font-bold text-gray-800">
                            {
                              category.name
                            }
                          </h2>

                        </div>

                        <div className="h-px bg-gray-100 flex-1" />

                      </div>

                      {/* SUB CATEGORIES */}

                      {category.subCategories.map(
                        (sub) => (

                          <div
                            key={
                              sub.id
                            }
                            ref={(element) => {
                              sectionRefs.current[
                                sub.id
                              ] = element;
                            }}
                            className="mb-8 scroll-mt-24"
                          >

                            {/* SUB TITLE */}

                            <div className="flex items-center justify-between mb-4">

                              <h3 className="text-base font-bold text-[#167a2c]">
                                {
                                  sub.name
                                }
                              </h3>

                              <button
                                type="button"
                                onClick={() =>
                                  handleSubCategoryClick(
                                    sub
                                  )
                                }
                                className="text-[11px] font-semibold text-[#35A93E] hover:underline"
                              >
                                View All →
                              </button>

                            </div>

                            {/* DIRECT PRODUCTS */}

                            {sub.products
                              .length >
                              0 && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

                                {sub.products.map(
                                  renderProductCard
                                )}

                              </div>
                            )}

                            {/* SUB SUB */}

                            {sub.subSubCategories.map(
                              (ss) => (

                                <div
                                  key={
                                    ss.id
                                  }
                                  ref={(element) => {
                                    sectionRefs.current[
                                      ss.id
                                    ] = element;
                                  }}
                                  className="mt-7 scroll-mt-24"
                                >

                                  <h4 className="text-sm font-bold text-gray-700 mb-4">
                                    {
                                      ss.name
                                    }
                                  </h4>

                                  {/* SUB SUB PRODUCTS */}

                                  {ss.products
                                    .length >
                                    0 && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

                                      {ss.products.map(
                                        renderProductCard
                                      )}

                                    </div>
                                  )}

                                  {/* SUB SUB SUB */}

                                  {ss.subSubSubCategories.map(
                                    (sss) => (

                                      <div
                                        key={
                                          sss.id
                                        }
                                        ref={(element) => {
                                          sectionRefs.current[
                                            sss.id
                                          ] =
                                            element;
                                        }}
                                        className="mt-7 scroll-mt-24"
                                      >

                                        <h5 className="text-xs font-bold uppercase tracking-wide text-[#35A93E] mb-4">
                                          {
                                            sss.name
                                          }
                                        </h5>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

                                          {sss.products.map(
                                            renderProductCard
                                          )}

                                        </div>

                                      </div>
                                    )
                                  )}

                                </div>
                              )
                            )}

                          </div>
                        )
                      )}

                    </section>
                  )
                )}

              </div>
            )}

          </main>
        </div>
      </div>
    </div>
  );
}

export default Shop;